import * as React from 'react';
import {
    NotificationsBadge,
    NotificationsWrapper,
    ChatBubbleIconWrapper,
    BetReferralIconWrapper,
    NotificationsCentreWrapper,
} from 'src/components/NotificationsCentre/NotificationsCentre.style';
import { NotificationsCentreState } from './NotificationsCentreState';
import { observer } from 'src/utils/mobx-react';
import { useEffect } from 'react';
import { autorun } from 'src_common/common/mobx-wrapper';
// @ts-expect-error
import * as dingMp3 from 'src/assets/ding.mp3';
// @ts-expect-error
import * as kachingMp3 from 'src/assets/kaching.mp3';
import { ChatNotificationsList } from 'src/components/NotificationsCentre/ChatRequestNotification/ChatNotificationsList';
import { BetReferralNotificationsList } from 'src/components/NotificationsCentre/BetReferralNotification/BetReferralNotificationsList';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { NotificationSoundConfig, NotificationSoundFile } from 'src/AppState/ConfigState/types';

const SOUND_FILES_MAP: Record<NotificationSoundFile, string> = {
    ding: dingMp3.default,
    kaching: kachingMp3.default,
};

export const playSound = (
    type: 'newChat' | 'newBetReferral',
    soundFiles: { newChat: NotificationSoundFile; newBetReferral: NotificationSoundFile }
): void => {
    try {
        const soundPath = type === 'newBetReferral' ? soundFiles.newBetReferral : soundFiles.newChat;
        const file = SOUND_FILES_MAP[soundPath];
        const audio = new Audio(window.origin + file);
        audio.play().catch((error) => {
            if (error.name === 'NotAllowedError') {
                console.warn('Notifications: Audio autoplay blocked by browser. User interaction required first.');
            } else {
                console.error('Audio playback error:', error);
            }
        });
    } catch (e) {
        console.error('Audio initialization error:', e);
    }
};

export const useHandleNotifications = (
    state: NotificationsCentreState,
    notificationSoundsConfig: NotificationSoundConfig
): void => {
    useEffect(() => {
        const dispose = autorun(() => {
            const notifications = state.notifications.data;
            const pendingNotifications = state.chatPendingNumber + state.referredPendingNumber;
            if (pendingNotifications > 0 && state.currentNumberOfNotifications < notifications.length) {
                const pendingChat = state.chatPendingNumber;
                const pendingReferred = state.referredPendingNumber;
                const newChat = pendingChat > state.prevChatPendingNumber;
                const newReferred = pendingReferred > state.prevReferredPendingNumber;
                if (newChat && newReferred) {
                    playSound('newChat', notificationSoundsConfig);
                    setTimeout(() => playSound('newBetReferral', notificationSoundsConfig), 2000);
                } else if (newChat) {
                    playSound('newChat', notificationSoundsConfig);
                } else if (newReferred) {
                    playSound('newBetReferral', notificationSoundsConfig);
                }
                state.showDesktopNotification(pendingChat, 'chat').catch((e) => console.error(e));
                state.showDesktopNotification(pendingReferred, 'betrefferal').catch((e) => console.error(e));
            }
            state.prevChatPendingNumber = state.chatPendingNumber;
            state.prevReferredPendingNumber = state.referredPendingNumber;
            state.currentNumberOfNotifications = notifications.length;
        });

        return dispose;
    }, []);
};

export const NotificationsCentre = observer('NotificationsCentre', () => {
    const common = useCommon();
    const state = NotificationsCentreState.get(common);
    const { notificationSounds } = ConfigState.get(common).config;

    useHandleNotifications(state, notificationSounds);

    return (
        <NotificationsCentreWrapper className='notifications'>
            <NotificationsWrapper onClick={state.toggleChatList}>
                <ChatBubbleIconWrapper />
                {state.chatPendingNumber > 0 ? (
                    <NotificationsBadge data-test='notifications-badge-chat'>
                        {state.chatPendingNumber}
                    </NotificationsBadge>
                ) : null}
            </NotificationsWrapper>
            {state.openChatList ? <ChatNotificationsList /> : null}
            <NotificationsWrapper onClick={state.toggleReferralList}>
                <BetReferralIconWrapper />
                {state.referredPendingNumber > 0 ? (
                    <NotificationsBadge data-test='notifications-badge-referred'>
                        {state.referredPendingNumber}
                    </NotificationsBadge>
                ) : null}
            </NotificationsWrapper>
            {state.openReferredList ? <BetReferralNotificationsList /> : null}
        </NotificationsCentreWrapper>
    );
});
